import React, { useEffect, useMemo, useState } from 'react';
import TemplateEditorModal from './TemplateEditorModal';
import { useNotification } from '../../../../components/framework/NotificationContext';
import { useConfirmation } from '../../../../contexts/ConfirmationContext';
import {
    getTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    toggleTemplateFavorite,
} from '../../../../services/emailTemplateService';

const CATEGORY_BADGE_CLASSES = {
    community: 'bg-primary-subtle text-primary',
    moderation: 'bg-warning-subtle text-warning',
    account: 'bg-info-subtle text-info',
    system: 'bg-secondary-subtle text-secondary',
    announcement: 'bg-success-subtle text-success',
    general: 'bg-light text-dark border',
};

const categoryBadgeClass = (category) => CATEGORY_BADGE_CLASSES[category] || 'bg-light text-dark border';

const TemplatesPanel = () => {
    const { success, error: notifyError } = useNotification();
    const confirm = useConfirmation();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        getTemplates()
            .then((data) => setTemplates(data?.content || data || []))
            .catch((err) => notifyError(err.message || 'Failed to load templates'))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const filtered = useMemo(() => {
        const term = search.toLowerCase().trim();
        return templates.filter((t) => {
            const matchesSearch = !term || t.name?.toLowerCase().includes(term) || t.description?.toLowerCase().includes(term);
            const matchesCategory = category === 'all' || t.category === category;
            return matchesSearch && matchesCategory;
        });
    }, [templates, search, category]);

    const categories = useMemo(() => ['all', ...new Set(templates.map((t) => t.category).filter(Boolean))], [templates]);

    const handleSave = async (template) => {
        setSaving(true);
        try {
            if (template.id) {
                await updateTemplate(template.id, template);
                success('Template updated');
            } else {
                await createTemplate(template);
                success('Template created');
            }
            setShowModal(false);
            setEditing(null);
            load();
        } catch (err) {
            notifyError(err.message || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (template) => {
        const ok = await confirm({ title: 'Delete template?', message: `Delete "${template.name}"? This cannot be undone.`, confirmLabel: 'Delete', type: 'danger' });
        if (!ok) return;
        try {
            await deleteTemplate(template.id);
            success('Template deleted');
            load();
        } catch (err) {
            notifyError(err.message || 'Failed to delete template');
        }
    };

    const handleDuplicate = async (template) => {
        try {
            await duplicateTemplate(template.id);
            success('Template duplicated');
            load();
        } catch (err) {
            notifyError(err.message || 'Failed to duplicate template');
        }
    };

    const handleFavorite = async (template) => {
        try {
            await toggleTemplateFavorite(template.id);
            load();
        } catch (err) {
            notifyError(err.message || 'Failed to update favorite');
        }
    };

    return (
        <div className="admin-card">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="mb-0"><i className="bi bi-file-earmark-richtext me-2"></i>Templates</h5>
                <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowModal(true); }}>
                    <i className="bi bi-plus-lg me-1"></i> New Template
                </button>
            </div>

            <div className="d-flex gap-2 mb-3 flex-wrap">
                <input className="form-control form-control-sm" style={{ maxWidth: '260px' }} placeholder="Search templates…" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select className="form-select form-select-sm" style={{ maxWidth: '200px' }} value={category} onChange={(e) => setCategory(e.target.value)}>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button className="btn btn-outline-secondary btn-sm" onClick={load}><i className="bi bi-arrow-clockwise"></i></button>
            </div>

            {loading ? (
                <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
            ) : (
                <div className="table-responsive">
                    <table className="admin-table">
                        <thead>
                            <tr><th>Name</th><th>Category</th><th>Favorite</th><th>Built-in</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan="5" className="text-center text-muted py-4">No templates found.</td></tr>
                            ) : filtered.map((t) => (
                                <tr key={t.id}>
                                    <td>{t.name}</td>
                                    <td><span className={`badge ${categoryBadgeClass(t.category)}`}>{t.category}</span></td>
                                    <td>
                                        <button className="btn btn-sm btn-link p-0" onClick={() => handleFavorite(t)}>
                                            <i className={`bi ${t.isFavorite ? 'bi-star-fill text-warning' : 'bi-star'}`}></i>
                                        </button>
                                    </td>
                                    <td>{t.isBuiltin ? <span className="badge bg-secondary-subtle text-secondary">Built-in</span> : '—'}</td>
                                    <td>
                                        <div className="d-flex gap-1 justify-content-end">
                                            <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditing(t); setShowModal(true); }} title="Edit"><i className="bi bi-pencil"></i></button>
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleDuplicate(t)} title="Duplicate"><i className="bi bi-files"></i></button>
                                            {!t.isBuiltin && <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(t)} title="Delete"><i className="bi bi-trash"></i></button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <TemplateEditorModal
                    template={editing}
                    saving={saving}
                    onSave={handleSave}
                    onClose={() => { setShowModal(false); setEditing(null); }}
                />
            )}
        </div>
    );
};

export default TemplatesPanel;
